import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Search, X, ChevronLeft, ChevronRight, ArrowLeft, Printer, FileText, Paperclip } from 'lucide-react';
import { Capitulo, ManualTokenProvider, COR, COR_ESCURA, COR_CLARA } from './BlocosManual';

// ============================================================================
// MANUAIS — o leitor
// ============================================================================
// Um capítulo por vez, com índice fixo à esquerda. NÃO é rolagem infinita do
// manual inteiro, e a razão é o conteúdo: são 82 páginas com 87 prints, e
// carregar tudo de uma vez seriam ~2 MB de imagem baixados para ler um passo.
// Capítulo por vez também é como a pessoa usa o documento — ela vem fazer UMA
// coisa (a sangria total, o recebimento do troco), não ler o manual de cabo a rabo.
//
// ⚠️ O CAPÍTULO ATIVO VAI PARA A URL (`?cap=sangria-parcial`). É o que permite
//    mandar "o processo está aqui" no WhatsApp e a pessoa cair no capítulo certo,
//    em vez de no começo do manual — que é o que um PDF de 82 páginas obriga.
//    Usamos `replaceState` e não o React Router porque o módulo não tem sub-rotas:
//    trocar de capítulo não deve empilhar entrada no histórico do navegador a
//    ponto de o "voltar" do celular precisar de 17 toques para sair do manual.
//
// ⚠️ A BUSCA olha título, objetivo, responsável e o texto dos blocos — não só o
//    título. Quem procura "banana", "malote" ou "testemunha" não sabe em qual
//    dos 17 capítulos a palavra está; era esse o problema do PDF.
// ============================================================================

const FONTE = "'Raleway', sans-serif";

// ⚠️ TETO DE LARGURA DA LEITURA. Sem ele o leitor acompanha a largura da tela, e
//    num monitor de 1920 isso dava três defeitos de uma vez: a capa virava uma
//    faixa marrom de ~1700px com o texto (travado em 620) ocupando só o primeiro
//    terço, a ficha do capítulo abria linhas de ~840px de texto corrido (o olho
//    perde a linha de volta) e o print, limitado a 420px, ficava perdido numa
//    coluna de 1390.
// ⚠️ O NÚMERO É ESCOLHA DE OLHO, NÃO DA MEDIDA DE LEITURA — e vale saber disso
//    antes de mexer nele (decidido com a Julia em 09/09/2026). 1450 é o que
//    quase preenche a área útil com o menu lateral ABERTO (~1534px num monitor
//    de 1920), de modo que não sobre degrau entre o fim do conteúdo e o fim da
//    barra branca do `ModuloHeader`, que é de largura cheia. Dois preços, os
//    dois aceitos na decisão: com o menu RECOLHIDO a área vai a ~1724 e o
//    degrau de ~270px reaparece; e a linha de texto do capítulo chega a
//    ~1090px, mais longa que os ~820 que a medida de leitura pediria.
//    ⚠️ Se o degrau incomodar, o conserto é capar o `ModuloHeader` nesta mesma
//       constante (em `Biblioteca.js`) — não é abaixar este número, que já foi
//       escolhido olhando a tela.
// ⚠️ Alinhado à ESQUERDA de propósito (sem `margin: auto`): o `ModuloHeader`
//    tem o título na esquerda — centrar o leitor criaria um degrau entre o
//    título e a capa.
const LARGURA_LEITURA = 1450;

/** Junta todo o texto pesquisável de um capítulo, em minúsculas e sem acento. */
function textoDoCapitulo(cap) {
    const partes = [cap.numero, cap.titulo, cap.objetivo, cap.responsavel, cap.quando];
    (cap.sistemas || []).forEach((s) => partes.push(s));
    (cap.blocos || []).forEach((b) => {
        partes.push(b.titulo, b.texto, b.depois, b.nota);
        (b.itens || []).forEach((i) => partes.push(i));
        (b.imagens || []).forEach((i) => partes.push(i.legenda));
        (b.linhas || []).forEach((l) => {
            // `linhas` é objeto no bloco `exemplo` e array no bloco `tabela`.
            if (Array.isArray(l)) l.forEach((c) => partes.push(c));
            else partes.push(l.rotulo, l.valor);
        });
        (b.colunas || []).forEach((c) => partes.push(c));
    });
    if (cap.conclusao) {
        partes.push(cap.conclusao.titulo);
        cap.conclusao.itens.forEach((i) => partes.push(i));
    }
    return normalizar(partes.filter(Boolean).join(' '));
}

// Busca sem acento: quem digita "atencao" tem de achar "atenção". `NFD` separa
// a letra do acento e o `replace` joga o acento fora.
// ⚠️ A faixa vai escrita ESCAPADA (\u0300-\u036f, os Combining Diacritical
//    Marks) e não com os caracteres literais: eles são invisíveis no editor, e um
//    deles perdido num salvamento com encoding errado quebraria a busca em
//    silêncio — a pessoa digitaria "atencao" e o manual diria que não achou.
const normalizar = (s) =>
    String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// `tokenUrl`/`rotuloVoltar` deixam o mesmo leitor servir Manuais e Conecta: o
// Conecta injeta um `manual` mapeado (ver conecta/mapearConecta.js) e aponta o
// token para a rota dele. Sem prop, cai no comportamento dos Manuais.
const ManualLeitor = ({ manual, onVoltar, isMobile, tokenUrl, rotuloVoltar = 'Todos os manuais' }) => {
    const urlToken = tokenUrl || `/api/biblioteca/manuais/${encodeURIComponent(manual.id)}/token`;
    const [busca, setBusca] = useState('');
    const [capAtivoId, setCapAtivoId] = useState(() => {
        // Capítulo da URL, se ele existir neste manual; senão o primeiro.
        const daUrl = new URLSearchParams(window.location.search).get('cap');
        const existe = manual.capitulos.some((c) => c.id === daUrl);
        return existe ? daUrl : manual.capitulos[0].id;
    });
    const [indiceAbertoMobile, setIndiceAbertoMobile] = useState(false);
    const topoRef = useRef(null);

    // Índice pesquisável, calculado uma vez por manual.
    const indice = useMemo(
        () => manual.capitulos.map((c) => ({ cap: c, texto: textoDoCapitulo(c) })),
        [manual]
    );

    const termo = normalizar(busca.trim());
    const filtrados = termo ? indice.filter((i) => i.texto.includes(termo)) : indice;

    const posicao = manual.capitulos.findIndex((c) => c.id === capAtivoId);
    const capAtivo = manual.capitulos[posicao] || manual.capitulos[0];
    const anterior = posicao > 0 ? manual.capitulos[posicao - 1] : null;
    const proximo = posicao < manual.capitulos.length - 1 ? manual.capitulos[posicao + 1] : null;

    const irPara = useCallback((id) => {
        setCapAtivoId(id);
        setIndiceAbertoMobile(false);
        // Volta ao topo do capítulo: sem isso, trocar de capítulo deixa a pessoa
        // no meio da página, olhando um passo que não é o primeiro.
        if (topoRef.current) topoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    /** Salto por NÚMERO de capítulo — é o que os blocos `referencia` usam. */
    const irPorNumero = useCallback((numero) => {
        const alvo = manual.capitulos.find((c) => c.numero === numero);
        if (alvo) irPara(alvo.id);
    }, [manual, irPara]);

    // Espelha o capítulo ativo na URL, sem empilhar histórico.
    useEffect(() => {
        const url = new URL(window.location.href);
        url.searchParams.set('cap', capAtivoId);
        window.history.replaceState(null, '', url);
    }, [capAtivoId]);

    return (
        <ManualTokenProvider tokenUrl={urlToken}>
            <div ref={topoRef} />

            {/* ================= capa / cabeçalho do manual ================= */}
            <div style={{
                background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
                borderRadius: 16, padding: isMobile ? 24 : 32, color: '#fff',
                marginBottom: 24, position: 'relative', overflow: 'hidden',
                maxWidth: LARGURA_LEITURA,
                boxShadow: `0 10px 15px -3px ${COR}4d`,
            }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <button
                        type="button"
                        onClick={onVoltar}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            marginBottom: 14, padding: '6px 12px', borderRadius: 8,
                            border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)',
                            color: '#fff', fontFamily: FONTE, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        }}
                    >
                        <ArrowLeft size={14} /> {rotuloVoltar}
                    </button>
                    <h2 style={{
                        margin: 0, fontFamily: FONTE, fontSize: isMobile ? 24 : 32,
                        fontWeight: 800, color: '#fff', lineHeight: 1.15,
                    }}>
                        {manual.titulo}
                    </h2>
                    <p style={{ margin: '8px 0 0', color: '#fde9d3', fontSize: isMobile ? 14 : 16, maxWidth: 620, lineHeight: 1.55 }}>
                        {manual.finalidade}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
                        {/* Manual vivo (ex.: Manual do Portal) não tem versão — a
                            etiqueta some em vez de mostrar "Versão  · ". */}
                        {manual.versao && <Etiqueta texto={`Versão ${manual.versao} · ${manual.ano}`} />}
                        {manual.aplicacao && <Etiqueta texto={manual.aplicacao} />}
                        <Etiqueta texto={`${manual.capitulos.length} capítulos`} />
                    </div>
                </div>
                <div style={{ position: 'absolute', right: -30, top: -40, opacity: 0.12 }}>
                    <FileText size={230} color="#ffffff" />
                </div>
            </div>

            {/* ================= corpo: índice + capítulo ================= */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '280px minmax(0, 1fr)',
                gap: 24, alignItems: 'start',
                maxWidth: LARGURA_LEITURA,
            }}>
                {/* ---------------- índice ---------------- */}
                <aside style={{
                    position: isMobile ? 'static' : 'sticky',
                    top: 16,
                    background: '#fff', border: '1px solid #e7e5e4', borderRadius: 12,
                    padding: 14, maxHeight: isMobile ? 'none' : 'calc(100vh - 48px)',
                    display: 'flex', flexDirection: 'column', minHeight: 0,
                }}>
                    {/* busca */}
                    <div style={{ position: 'relative', marginBottom: 12 }}>
                        <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: '#a8a29e' }} />
                        <input
                            type="text"
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                            placeholder="Buscar no manual…"
                            style={{
                                width: '100%', boxSizing: 'border-box',
                                padding: '8px 30px 8px 32px', borderRadius: 8,
                                border: '1px solid #e7e5e4', fontFamily: FONTE, fontSize: 13.5,
                                outline: 'none', color: '#292524',
                            }}
                        />
                        {busca && (
                            <button
                                type="button"
                                onClick={() => setBusca('')}
                                aria-label="Limpar busca"
                                style={{
                                    position: 'absolute', right: 6, top: 6, width: 22, height: 22,
                                    border: 'none', background: 'transparent', cursor: 'pointer',
                                    color: '#a8a29e', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {/* No celular o índice fica recolhido: 17 capítulos empurrariam
                        o conteúdo para fora da primeira tela. */}
                    {isMobile && (
                        <button
                            type="button"
                            onClick={() => setIndiceAbertoMobile((v) => !v)}
                            style={{
                                marginBottom: indiceAbertoMobile ? 10 : 0, padding: '8px 12px',
                                borderRadius: 8, border: `1px solid ${COR}33`, background: COR_CLARA,
                                color: COR_ESCURA, fontFamily: FONTE, fontSize: 13, fontWeight: 700,
                                cursor: 'pointer', textAlign: 'left',
                            }}
                        >
                            {indiceAbertoMobile ? 'Ocultar' : 'Ver'} índice ({filtrados.length})
                        </button>
                    )}

                    {(!isMobile || indiceAbertoMobile || termo) && (
                        <nav style={{ overflowY: 'auto', minHeight: 0, margin: '0 -4px', padding: '0 4px' }}>
                            {filtrados.length === 0 && (
                                <p style={{ margin: '8px 4px', fontSize: 13, color: '#78716c', lineHeight: 1.5 }}>
                                    Nenhum capítulo menciona “{busca.trim()}”.
                                </p>
                            )}
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 2 }}>
                                {filtrados.map(({ cap }) => {
                                    const ativo = cap.id === capAtivoId;
                                    return (
                                        <li key={cap.id}>
                                            <button
                                                type="button"
                                                onClick={() => irPara(cap.id)}
                                                style={{
                                                    width: '100%', display: 'flex', gap: 9, alignItems: 'flex-start',
                                                    padding: '8px 9px', borderRadius: 8, border: 'none',
                                                    background: ativo ? COR_CLARA : 'transparent',
                                                    cursor: 'pointer', textAlign: 'left', fontFamily: FONTE,
                                                }}
                                            >
                                                <span style={{
                                                    flexShrink: 0, fontSize: 11.5, fontWeight: 800,
                                                    fontVariantNumeric: 'tabular-nums',
                                                    color: ativo ? COR : '#a8a29e', marginTop: 2,
                                                }}>
                                                    {cap.anexo ? <Paperclip size={12} /> : cap.numero}
                                                </span>
                                                <span style={{
                                                    fontSize: 13.5, lineHeight: 1.4,
                                                    fontWeight: ativo ? 800 : 500,
                                                    color: ativo ? COR_ESCURA : '#44403c',
                                                }}>
                                                    {cap.titulo}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </nav>
                    )}

                    {/* Impressão: manda a janela imprimir a página. O manual é
                        documento de parede na gerência — imprimir o capítulo é
                        uso real, não recurso decorativo. */}
                    {!isMobile && (
                        <button
                            type="button"
                            onClick={() => window.print()}
                            style={{
                                marginTop: 12, padding: '8px 12px', borderRadius: 8,
                                border: '1px solid #e7e5e4', background: '#fafaf9',
                                color: '#57534e', fontFamily: FONTE, fontSize: 13,
                                fontWeight: 700, cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                            }}
                        >
                            <Printer size={14} /> Imprimir este capítulo
                        </button>
                    )}
                </aside>

                {/* ---------------- o capítulo ---------------- */}
                <main style={{
                    background: '#fdfcfb', border: '1px solid #e7e5e4', borderRadius: 12,
                    padding: isMobile ? 18 : 28, minWidth: 0,
                }}>
                    <Capitulo capitulo={capAtivo} onIrParaCapitulo={irPorNumero} />

                    {/* navegação sequencial — para quem está lendo o manual inteiro */}
                    <nav style={{
                        display: 'flex', gap: 12, marginTop: 36, paddingTop: 20,
                        borderTop: '1px solid #e7e5e4',
                        flexDirection: isMobile ? 'column' : 'row',
                    }}>
                        {anterior ? (
                            <BotaoNav
                                sentido="anterior"
                                cap={anterior}
                                onClick={() => irPara(anterior.id)}
                            />
                        ) : <div style={{ flex: 1 }} />}
                        {proximo ? (
                            <BotaoNav
                                sentido="proximo"
                                cap={proximo}
                                onClick={() => irPara(proximo.id)}
                            />
                        ) : <div style={{ flex: 1 }} />}
                    </nav>
                </main>
            </div>

            {/* Rodapé com o controle do documento — é o que o manual impresso
                traz na 2ª página, e some se ninguém o transportar para cá. */}
            <footer style={{
                marginTop: 24, padding: '14px 18px', borderRadius: 12,
                background: '#fff', border: '1px solid #e7e5e4',
                fontSize: 12.5, lineHeight: 1.6, color: '#78716c',
                maxWidth: LARGURA_LEITURA,
            }}>
                <strong style={{ color: '#57534e' }}>Controle do documento</strong> — {manual.titulo}
                {manual.versao ? <>, versão {manual.versao} ({manual.ano})</> : ''}. {manual.aplicacao}.
                {manual.padrao && <> {manual.padrao}</>}
            </footer>
        </ManualTokenProvider>
    );
};

function Etiqueta({ texto }) {
    return (
        <span style={{
            padding: '4px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.25)', color: '#fff',
            fontFamily: FONTE, fontSize: 12, fontWeight: 700,
        }}>
            {texto}
        </span>
    );
}

function BotaoNav({ sentido, cap, onClick }) {
    const ehAnterior = sentido === 'anterior';
    const Icone = ehAnterior ? ChevronLeft : ChevronRight;
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                justifyContent: ehAnterior ? 'flex-start' : 'flex-end',
                padding: '12px 14px', borderRadius: 10, border: '1px solid #e7e5e4',
                background: '#fff', cursor: 'pointer', textAlign: ehAnterior ? 'left' : 'right',
                fontFamily: FONTE, minWidth: 0,
            }}
        >
            {ehAnterior && <Icone size={18} style={{ color: COR, flexShrink: 0 }} />}
            <span style={{ minWidth: 0 }}>
                <span style={{
                    display: 'block', fontSize: 11, fontWeight: 800, color: '#a8a29e',
                    textTransform: 'uppercase', letterSpacing: 0.6,
                }}>
                    {ehAnterior ? 'Anterior' : 'Próximo'}
                </span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#292524' }}>
                    {cap.titulo}
                </span>
            </span>
            {!ehAnterior && <Icone size={18} style={{ color: COR, flexShrink: 0 }} />}
        </button>
    );
}

export default ManualLeitor;
